export function rootIdsOf(nodes) {
  const ids = new Set(nodes.map((n) => n.id));
  return nodes.filter((n) => !n.parentId || !ids.has(n.parentId)).map((n) => n.id);
}

export function buildForest(nodes) {
  const byParent = new Map();
  nodes.forEach((node) => {
    if (!node.parentId) return;
    const arr = byParent.get(node.parentId) ?? [];
    arr.push(node);
    byParent.set(node.parentId, arr);
  });
  const seen = new Set();
  const make = (id, depth) => {
    seen.add(id);
    return {
      id,
      depth,
      children: (byParent.get(id) ?? [])
        .filter((c) => !seen.has(c.id))
        .map((c) => make(c.id, depth + 1)),
    };
  };
  return rootIdsOf(nodes).map((id) => make(id, 0));
}

const SLOT_X = 244;
const SLOT_Y = 164;
const RING = 200;
const RING_OFFSET = 95;
const H_COL_W = 328;
const H_LEAF_H = 134;
const ROOT_GAP = 0.55;

function recenter(pos) {
  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
  pos.forEach((p) => {
    minX = Math.min(minX, p.x); maxX = Math.max(maxX, p.x);
    minY = Math.min(minY, p.y); maxY = Math.max(maxY, p.y);
  });
  const cx = (minX + maxX) / 2;
  const cy = (minY + maxY) / 2;
  const out = new Map();
  pos.forEach((p, id) => out.set(id, { x: p.x - cx, y: p.y - cy }));
  return out;
}

export function getDepths(nodes) {
  const map = new Map();
  const walk = (t) => { map.set(t.id, t.depth); t.children.forEach(walk); };
  buildForest(nodes).forEach(walk);
  return map;
}

export function computeLayout(nodes, mode, scale = 1, factors) {
  const forest = buildForest(nodes);
  const pos = new Map();
  if (!forest.length) return pos;

  const f = (id) => factors?.get(id) ?? 1;
  const multi = forest.length > 1;
  const eff = (d) => (multi && mode === "radial" ? d + 1 : d);

  const depthFactor = [];
  let maxDepth = 0;
  const scout = (t) => {
    const d = eff(t.depth);
    maxDepth = Math.max(maxDepth, d);
    depthFactor[d] = Math.max(depthFactor[d] ?? 0, f(t.id));
    t.children.forEach(scout);
  };
  forest.forEach(scout);

  if (mode === "radial") {
    const radii = [0];
    for (let k = 1; k <= maxDepth; k++) {
      radii[k] = (k === 1 ? RING_OFFSET * scale : radii[k - 1]) + RING * scale * depthFactor[k];
    }
    const weight = (t) =>
      t.children.length === 0 ? f(t.id) : t.children.reduce((s, c) => s + weight(c), 0);
    const total = forest.reduce((s, r) => s + weight(r), 0) || 1;
    const TAU = Math.PI * 2;
    const assign = (t, a0) => {
      const span = (weight(t) / total) * TAU;
      const a = a0 + span / 2;
      const r = radii[eff(t.depth)];
      pos.set(t.id, { x: Math.cos(a) * r, y: Math.sin(a) * r });
      let acc = a0;
      t.children.forEach((c) => {
        assign(c, acc);
        acc += (weight(c) / total) * TAU;
      });
    };
    let cursor = -Math.PI / 2;
    forest.forEach((root) => {
      assign(root, cursor);
      cursor += (weight(root) / total) * TAU;
    });
    return recenter(pos);
  }

  if (mode === "vertical" || mode === "inverted") {
    const rowH = (k) => SLOT_Y * scale * (depthFactor[k] ?? 1);
    const rowCenter = [];
    let accY = 0;
    for (let k = 0; k <= maxDepth; k++) {
      rowCenter[k] = accY + rowH(k) / 2;
      accY += rowH(k);
    }
    const totalH = accY;

    let cursor = 0;
    const place = (t) => {
      if (t.children.length === 0) {
        const w = SLOT_X * scale * f(t.id);
        const cx = cursor + w / 2;
        cursor += w;
        pos.set(t.id, { x: cx, y: rowCenter[t.depth] });
        return cx;
      }
      const xs = t.children.map(place);
      const cx = (Math.min(...xs) + Math.max(...xs)) / 2;
      pos.set(t.id, { x: cx, y: rowCenter[t.depth] });
      return cx;
    };
    forest.forEach((root, i) => {
      if (i > 0) cursor += SLOT_X * scale * ROOT_GAP;
      place(root);
    });

    if (mode === "vertical") return recenter(pos);
    const out = new Map();
    pos.forEach((p, id) => out.set(id, { x: p.x, y: totalH - p.y }));
    return recenter(out);
  }

  const colW = (k) => H_COL_W * scale * (depthFactor[k] ?? 1);
  const colCenter = [];
  let accX = 0;
  for (let k = 0; k <= maxDepth; k++) {
    colCenter[k] = accX + colW(k) / 2;
    accX += colW(k);
  }

  let cursorY = 0;
  const place = (t) => {
    if (t.children.length === 0) {
      const h = H_LEAF_H * scale * f(t.id);
      const cy = cursorY + h / 2;
      cursorY += h;
      pos.set(t.id, { x: colCenter[t.depth], y: cy });
      return cy;
    }
    const ys = t.children.map(place);
    const cy = (Math.min(...ys) + Math.max(...ys)) / 2;
    pos.set(t.id, { x: colCenter[t.depth], y: cy });
    return cy;
  };
  forest.forEach((root, i) => {
    if (i > 0) cursorY += H_LEAF_H * scale * ROOT_GAP;
    place(root);
  });
  return recenter(pos);
}

// Orthogonal (Manhattan) connector — only vertical + horizontal segments, no curves.
// anchor: "left" | "center" | "right" — where the line attaches to the card edge.
//   - In vertical/inverted/radial layouts the offset is horizontal along the top/bottom edge.
//   - In horizontal layout the offset is vertical along the left/right edge.
const CARD_HALF_W = 108; // CARD_W / 2
const CARD_HALF_H = 39;  // CARD_H / 2
const ANCHOR_FACTOR = { left: -1, center: 0, right: 1 };

export function edgePath(px, py, cx, cy, parentAnchor = "center", childAnchor = "center", mode = "vertical") {
  const pa = ANCHOR_FACTOR[parentAnchor] ?? 0;
  const ca = ANCHOR_FACTOR[childAnchor] ?? 0;

  if (mode === "horizontal") {
    const toRight = cx >= px;
    const sx = toRight ? px + CARD_HALF_W : px - CARD_HALF_W;
    const sy = py + pa * CARD_HALF_H;
    const ex = toRight ? cx - CARD_HALF_W : cx + CARD_HALF_W;
    const ey = cy + ca * CARD_HALF_H;
    const midX = (sx + ex) / 2;
    return `M ${sx.toFixed(1)} ${sy.toFixed(1)} H ${midX.toFixed(1)} V ${ey.toFixed(1)} H ${ex.toFixed(1)}`;
  }

  // vertical / inverted / radial
  const below = cy >= py;
  const sx = px + pa * CARD_HALF_W;
  const sy = below ? py + CARD_HALF_H : py - CARD_HALF_H;
  const ex = cx + ca * CARD_HALF_W;
  const ey = below ? cy - CARD_HALF_H : cy + CARD_HALF_H;
  const midY = (sy + ey) / 2;
  return `M ${sx.toFixed(1)} ${sy.toFixed(1)} V ${midY.toFixed(1)} H ${ex.toFixed(1)} V ${ey.toFixed(1)}`;
}

export function subtreeIds(nodes, id) {
  const ids = new Set();
  const queue = [id];
  while (queue.length) {
    const cur = queue.pop();
    ids.add(cur);
    nodes.forEach((n) => { if (n.parentId === cur && !ids.has(n.id)) queue.push(n.id); });
  }
  return ids;
}

export function ancestorIds(nodes, id) {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const ids = new Set();
  let cur = byId.get(id);
  while (cur && !ids.has(cur.id)) {
    ids.add(cur.id);
    cur = cur.parentId ? byId.get(cur.parentId) : undefined;
  }
  return ids;
}

export function nodeBounds(targets) {
  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
  targets.forEach((p) => {
    minX = Math.min(minX, p.x); maxX = Math.max(maxX, p.x);
    minY = Math.min(minY, p.y); maxY = Math.max(maxY, p.y);
  });
  if (!isFinite(minX)) { minX = -200; maxX = 200; minY = -120; maxY = 120; }
  return { minX, maxX, minY, maxY, w: maxX - minX, h: maxY - minY };
}
